/**
 * Retry utilities with exponential backoff and jitter.
 *
 * The default {@link isRetryableError} predicate retries transient failures —
 * network/fetch errors ("Failed to fetch" / "Load failed"), HTTP 5xx, and 429 —
 * but NOT 4xx client errors (400/401/403/404/validation), which will never
 * succeed on retry and should surface to the caller immediately.
 */

/** Anything we can pull an HTTP-ish status out of. */
type StatusLike = { status?: unknown; code?: unknown; message?: unknown };

const NETWORK_ERROR_PATTERN = /failed to fetch|load failed|networkerror|network request failed|the server could not be reached/i;

/** Extracts a numeric HTTP status from common error shapes (status, code, or "HTTP 5xx" message). */
const extractStatus = (error: unknown): number | undefined => {
	if (error && typeof error === "object") {
		const candidate = error as StatusLike;
		const numericStatus = Number(candidate.status ?? candidate.code);
		if (Number.isFinite(numericStatus) && numericStatus >= 100 && numericStatus <= 599) {
			return numericStatus;
		}
		if (typeof candidate.message === "string") {
			const match = candidate.message.match(/\bHTTP\s+(\d{3})\b/i);
			if (match) return Number(match[1]);
		}
	}
	return undefined;
};

/**
 * Default retry predicate for transient REC-start/upload fetch failures.
 *
 * Retryable: network/fetch failures, HTTP 5xx, HTTP 429, and errors with no
 * discernible status (fail-open — an undecodable error is more often a transient
 * network blip than a deterministic client error). Not retryable: any other 4xx.
 */
export const isRetryableError = (error: unknown): boolean => {
	const status = extractStatus(error);
	if (status !== undefined) {
		if (status === 429) return true;
		return status >= 500;
	}

	const message = error instanceof Error ? error.message : typeof error === "string" ? error : (error as StatusLike)?.message;
	if (typeof message === "string" && NETWORK_ERROR_PATTERN.test(message)) {
		return true;
	}

	// No status, no recognized network signature: treat as transient and allow a retry.
	return true;
};

export type RetryOptions = {
	/** Number of retries AFTER the initial attempt (default: 3). */
	retries?: number;
	/** Base delay in milliseconds for the exponential backoff (default: 1000). */
	baseDelayMs?: number;
	/** Predicate deciding whether a thrown error is worth retrying (default: {@link isRetryableError}). */
	isRetryable?: (error: unknown) => boolean;
	/** Injectable sleep — primarily for deterministic tests (default: setTimeout + jitter). */
	sleep?: (ms: number) => Promise<void>;
};

const defaultSleep = (ms: number): Promise<void> =>
	// Exponential backoff already supplies `ms`; add jitter to avoid thundering-herd retries.
	// Note: this is additive (0-1s) jitter, kept as-is (LSP-17090 finding 2 — switching this shape
	// would accelerate retry pressure for every existing consumer of retryWithBackoff: presignedUrl,
	// sessionSettings, scribe). Reconnect-style loops (SSE, proactive token refresh) should use the
	// multiplicative {@link backoffDelayMs} below instead.
	new Promise((resolve) => setTimeout(resolve, ms + Math.random() * 1000));

/**
 * Computes a jittered exponential backoff delay: `min(baseMs * 2^attempt, maxMs)` scaled by a
 * random multiplicative factor in [0.5, 1).
 *
 * A flat or unjittered exponential delay causes every client to retry in tenant-wide lockstep —
 * after any shared-service blip (SSE reconnects against scribe, proactive token refresh against
 * Auth0), the whole tenant hammers the service at the same instant on every attempt, which can
 * itself cause the outage it's retrying from (see LSP-17085 and the Jul 2026 Auth0 rate-limit
 * incident referenced in auth.js). Multiplicative 50-100% jitter spreads that herd across a window
 * instead of a single instant. This is the canonical jittered-backoff convention for LSP-17090.
 *
 * @param attempt 0-based attempt number (0 = first retry).
 * @param baseMs base delay in milliseconds before jitter/cap.
 * @param maxMs cap on the delay (applied before jitter, so the jittered result stays in [0.5*maxMs, maxMs]).
 * @param random injectable random source for deterministic tests (default: Math.random).
 */
export const backoffDelayMs = (attempt: number, baseMs: number, maxMs: number, random: () => number = Math.random): number =>
	Math.floor(Math.min(baseMs * 2 ** attempt, maxMs) * (0.5 + random() * 0.5));

/**
 * Retry an async operation with exponential backoff.
 *
 * Supports two call shapes:
 *  - Options object:    retryWithBackoff(op, { retries, baseDelayMs, isRetryable })
 *  - Legacy positional: retryWithBackoff(op, maxRetries, baseDelay)
 *
 * The legacy positional form retries on ANY error (no `isRetryable` gate), preserving
 * the behavior existing callers relied on before this predicate was introduced.
 *
 * @returns the operation result, or rejects with the last error once retries are exhausted
 *          (or immediately if the error is not retryable).
 */
export async function retryWithBackoff<T>(operation: () => Promise<T>, options?: RetryOptions): Promise<T>;
export async function retryWithBackoff<T>(operation: () => Promise<T>, maxRetries?: number, baseDelay?: number): Promise<T>;
export async function retryWithBackoff<T>(
	operation: () => Promise<T>,
	optionsOrMaxRetries: RetryOptions | number = {},
	legacyBaseDelay?: number
): Promise<T> {
	const isLegacyPositional = typeof optionsOrMaxRetries === "number";
	const retries = isLegacyPositional ? optionsOrMaxRetries : (optionsOrMaxRetries.retries ?? 3);
	const baseDelayMs = isLegacyPositional ? (legacyBaseDelay ?? 1000) : (optionsOrMaxRetries.baseDelayMs ?? 1000);
	// Legacy callers expect every error to be retried; new callers get the transient-only gate.
	const isRetryable = isLegacyPositional ? () => true : (optionsOrMaxRetries.isRetryable ?? isRetryableError);
	const sleep = isLegacyPositional ? defaultSleep : (optionsOrMaxRetries.sleep ?? defaultSleep);

	let lastError: unknown;

	for (let attempt = 0; attempt <= retries; attempt++) {
		try {
			return await operation();
		} catch (error) {
			lastError = error;

			if (attempt === retries || !isRetryable(error)) {
				throw error;
			}

			console.warn(`retryWithBackoff: attempt ${attempt + 1} failed, retrying`, error);
			await sleep(baseDelayMs * Math.pow(2, attempt));
		}
	}

	throw lastError;
}

/**
 * Convenience wrapper that always takes an options object.
 */
export const retry = <T>(operation: () => Promise<T>, options: RetryOptions = {}): Promise<T> => retryWithBackoff(operation, options);
